import dayjs, { Dayjs } from "dayjs";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

const DATE_URL_FORMAT = "YYYY-MM-DD";

export const getRelativeDateRange = (
    referenceDay: Dayjs,
    daysBefore: number,
    daysAfter: number
): Dayjs[] => {
    const totalDays = daysBefore + daysAfter + 1;
    return Array.from({ length: totalDays }, (_, i) => referenceDay.subtract(daysBefore, "day").add(i, "day"));
};

export function useDateRangeQueryParams(daysBefore = 3, daysAfter = 3) {
    const [searchParams, setSearchParams] = useSearchParams();

    const fromDateParam = searchParams.get("fromDate");
    const toDateParam = searchParams.get("toDate");

    const referenceDay = useMemo(() => {
        return fromDateParam ? dayjs(fromDateParam, DATE_URL_FORMAT).add(daysBefore, "day") : dayjs();
    }, [fromDateParam]);

    const dateRange = useMemo(() => {
        return getRelativeDateRange(referenceDay, daysBefore, daysAfter);
    }, [referenceDay, daysBefore, daysAfter]);

    const setNewDateSearchParams = useCallback(
        (day: dayjs.Dayjs) => {
            const dates = getRelativeDateRange(day, daysBefore, daysAfter);
            const UrlFromDate = dates[0].format(DATE_URL_FORMAT);
            const UrlToDate = dates[dates.length - 1].format(DATE_URL_FORMAT);
            setSearchParams((prev) => {
                prev.set("fromDate", UrlFromDate);
                prev.set("toDate", UrlToDate);
                return prev;
            });

        },
        [searchParams, daysBefore, daysAfter]
    );

    const prev = useCallback(() => {
        const newDate = referenceDay.subtract(1, "day");
        setNewDateSearchParams(newDate);
    }, [referenceDay, setNewDateSearchParams]);

    const next = useCallback(() => {
        const newDate = referenceDay.add(1, "day");
        setNewDateSearchParams(newDate);
    }, [referenceDay, setNewDateSearchParams]);

    const goToDateRange = useCallback(
        (day: dayjs.Dayjs) => setNewDateSearchParams(day),
        [setNewDateSearchParams, setSearchParams]
    );

    const firstDayInRange = dateRange[0];
    const lastDayInRange = dateRange[dateRange.length - 1];

    return {
        fromDateParam,
        toDateParam,
        referenceDay,
        dateRange,
        goToDateRange,
        firstDayInRange,
        lastDayInRange,
        prev,
        next
    };
}
